"use client";
import React, { useEffect, useState, useRef } from "react";
import Image from "next/image";
import { apiFetch } from "@/utils/api";
import { getSafeImageUrl } from "@/utils/images";
import Carousel from "./ui/Carousel";
import {
  Plane,
  UtensilsCrossed,
  Ticket,
  Tv,
  Heart,
  Shirt,
  Star,
  Search,
  SlidersHorizontal,
  ChevronDown,
  ChevronUp,
  Check,
} from "lucide-react";
import LoadingState from "@/components/ui/LoadingState";
import EmptyState from "@/components/ui/EmptyState";
import OfferCard from "@/components/ui/OfferCard";
import Link from "next/link";
import { ArrowRightIcon } from "@/utils/icons";
import { useRouter } from "next/navigation";

interface CrousalBanner {
  _id?: string;
  media?: string;
  alt?: string;
  href?: string;
  order: number;
  isActive: boolean;
  isFeatured: boolean;
  HTMLcode?: string;
  media_link?: string;
  orientation: "horizontal" | "vertical";
}

const AdBanner = () => {
  const [banners, setBanners] = useState<CrousalBanner[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const autoScrollRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    setLoading(true);
    apiFetch<{ success: boolean; data: CrousalBanner[] }>(
      "/crousal-banners?orientation=horizontal&isFeatured=true&isActive=true"
    )
      .then((res) => {
        const bannersArr: CrousalBanner[] = Array.isArray(res.data) ? res.data : [];

        if (bannersArr.length > 0) {
          setBanners(bannersArr);
          setError(null);
        } else {
          setBanners(null);
          setError("No featured ad banner found.");
        }
      })
      .catch(() => {
        setError("Failed to load ad banner.");
        setBanners(null);
      })
      .finally(() => setLoading(false));
  }, []);

  // Auto-scroll effect
  useEffect(() => {
    if (!banners || banners.length <= 1) return;

    const startAutoScroll = () => {
      autoScrollRef.current = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % banners.length);
      }, 3500);
    };

    startAutoScroll();
    return () => {
      if (autoScrollRef.current) clearInterval(autoScrollRef.current);
    };
  }, [banners]);

  const resetAutoScroll = () => {
    if (autoScrollRef.current) clearInterval(autoScrollRef.current);
    autoScrollRef.current = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % (banners?.length || 1));
    }, 3500);
  };

  const goToPrevious = () => {
    if (!banners) return;
    setCurrentIndex((prev) => (prev - 1 + banners.length) % banners.length);
    resetAutoScroll();
  };

  const goToNext = () => {
    if (!banners) return;
    setCurrentIndex((prev) => (prev + 1) % banners.length);
    resetAutoScroll();
  };

  const goToSlide = (index: number) => {
    setCurrentIndex(index);
    resetAutoScroll();
  };

  if (loading) {
    return <div className="w-full h-20 md:h-32 bg-gray-100 rounded-xl flex items-center justify-center text-sm text-gray-500 mb-6">Loading Ad Banner...</div>;
  }

  if (error || !banners || banners.length === 0) {
    return (
      <div className="mb-8 rounded-xl overflow-hidden shadow-md bg-gray-50 border border-gray-200">
        <a href="#" className="block" aria-label="Advertisement Placeholder">
          <div className="relative w-full h-24 md:h-40 lg:h-48 flex items-center justify-center">
            <span className="text-gray-500 font-medium text-sm md:text-base">Advertisement Placeholder</span>
          </div>
        </a>
      </div>
    );
  }

  const currentBanner = banners[currentIndex];
  const imageUrl =
    (currentBanner.media_link && currentBanner.media_link.trim() !== "")
      ? currentBanner.media_link
      : currentBanner.media || "/assets/OfferCodeFlight.jpg";

  return (
    <div className="mb-8 rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300">
      {/* Carousel Container */}
      <div className="relative w-full group">
        {/* Slide Container */}
        <div className="relative w-full overflow-hidden rounded-xl">
          <div
            className="transition-opacity duration-500 ease-in-out"
            style={{ opacity: 1 }}
          >
            {currentBanner.HTMLcode && currentBanner.HTMLcode.trim() !== '' ? (
              <div
                className={`relative w-full p-0 ${
                  currentBanner.orientation === "vertical" 
                    ? "h-[360px]" 
                    : "h-[150px] md:h-[200px] lg:h-[240px]" 
                } rounded-md overflow-hidden `}
                dangerouslySetInnerHTML={{ __html: currentBanner.HTMLcode }}
              />
            ) : (
              <a href={currentBanner.href || "#"} target="_blank" rel="noopener noreferrer" className="block" aria-label={currentBanner.alt || "Advertisement"}>
                <div className="relative w-full h-[150px] md:h-[200px] lg:h-[240px]">
                  <Image
                    src={getSafeImageUrl(imageUrl) || "/assets/OfferCodeFlight.jpg"}
                    alt={currentBanner.alt || "Advertisement"}
                    fill
                    className=" w-full h-full"
                    priority={false}
                    onError={(e) => {
                      e.currentTarget.srcset = "";
                      e.currentTarget.src = "/assets/default-ad-banner.jpg";
                    }}
                  />
                </div>
              </a>
            )}
          </div>
        </div>

        {/* Navigation Buttons */}
        {banners.length > 1 && (
          <>
            {/* Previous Button */}
            <button
              onClick={goToPrevious}
              className="absolute left-3 top-1/2 -translate-y-1/2 z-10 bg-black/40 hover:bg-black/60 text-white rounded-full p-2 transition-all duration-300 opacity-30 group-hover:opacity-70"
              aria-label="Previous banner"
            >
              <svg className="w-5 h-5 md:w-6 md:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>

            {/* Next Button */}
            <button
              onClick={goToNext}
              className="absolute right-3 top-1/2 -translate-y-1/2 z-10 bg-black/40 hover:bg-black/60 text-white rounded-full p-2 transition-all duration-300 opacity-30 group-hover:opacity-70"
              aria-label="Next banner"
            >
              <svg className="w-5 h-5 md:w-6 md:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>

            {/* Dot Indicators */}
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-10 flex gap-2">
              {banners.map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToSlide(index)}
                  className={`transition-all duration-300 rounded-full ${
                    index === currentIndex
                      ? "bg-white w-2 h-2 md:w-3 md:h-3"
                      : "bg-white/50 hover:bg-white/75 w-2 h-2 md:w-2 md:h-2"
                  }`}
                  aria-label={`Go to banner ${index + 1}`}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const CATEGORY_ICONS: Record<string, React.ReactNode> = {
  fashion: <Shirt className="w-6 h-6" />,
  beauty: <Heart className="w-6 h-6" />,
  food: <UtensilsCrossed className="w-6 h-6" />,
  travel: <Plane className="w-6 h-6" />,
  "web-hosting": <Tv className="w-6 h-6" />,
  entertainment: <Ticket className="w-6 h-6" />,
};

interface Offer {
  _id: string;
  title: string;
  store: string;
  storeIcon?: string;
  couponCode: string;
  validTill: string;
  isActive: boolean;
  description?: string;
  discountType: string;
  discountTypeFlat: string;
  discount: string;
  discountTypeUnit: string;
  oldValue: string;
  oldValueUnit: "price" | "percentage";
  badge?: string;
  slug?: string;
  rating?: number;
  allowRedirectUrl?: boolean;
  redirectUrl?: string;
  usedBy?: number;
}

interface Store {
  _id?: string;
  name: string;
  slug: string;
  icon?: string;
  description?: string;
  website?: string;
  category?: string;
  isActive?: boolean;
  offersCount?: number;
  coupons?: number;
}

interface BankWalletOffer {
  _id?: string;
  bankName: string;
  offerType: string;
  title: string;
  slug: string;
  description?: string;
  discount?: string;
  maxDiscount?: string;
  validTill?: string;
  color?: string;
  icon?: string;
  validOn?: string;
  isActive: boolean;
  category: string;
}

interface Category {
  _id?: string;
  name: string;
  isActive: boolean;
  slug: string;
  icon?: string;
  description?: string;
}

interface MobileSortDropdownProps {
  currentSort: string;
  onSortChange: (newSort: string) => void;
}

const MobileSortDropdown: React.FC<MobileSortDropdownProps> = ({ currentSort, onSortChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close the dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [dropdownRef]);

  const sortOptions = [
    { value: "usedby-desc", label: "Most Used" },
    { value: "rating-desc", label: "Highest Rated" },
    { value: "title-asc", label: "Title (A-Z)" },
  ];

  const handleSelect = (value: string) => {
    onSortChange(value);
    setIsOpen(false);
  };

  return (
    <div className="relative flex-shrink-0" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="md:hidden flex items-center gap-2 pl-4 pr-3 py-2 border border-gray-300 rounded-lg bg-white text-sm text-gray-700 hover:bg-gray-50 transition-colors w-full"
      >
        <SlidersHorizontal className="w-4 h-4 text-gray-500" />
        <span>{sortOptions.find(o => o.value === currentSort)?.label || 'Sort Options'}</span>
        {isOpen ? <ChevronUp className="w-4 h-4 ml-1" /> : <ChevronDown className="w-4 h-4 ml-1" />}
      </button>

      {isOpen && (
        <div
          className="absolute z-40 right-0 mt-2 w-48 rounded-lg shadow-xl bg-white border border-gray-200"
        >
          <div className="py-1">
            {sortOptions.map((option) => (
              <button
                key={option.value}
                onClick={() => handleSelect(option.value)}
                className={`w-full text-left px-4 py-2 text-sm flex justify-between items-center transition-colors duration-150 ${currentSort === option.value
                    ? "bg-primary-50 text-primary-600 font-medium"
                    : "hover:bg-gray-100 text-gray-700"
                  }`}
              >
                {option.label}
                {currentSort === option.value && (
                  <Check className="w-4 h-4" />
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const MOBILE_OFFER_LIMIT = 4;

export default function CategoriesSection() {
  const [categories, setCategories] = useState<Category[]>([]);
  const [active, setActive] = useState<string>("");
  const [offers, setOffers] = useState<Offer[]>([]);
  // const [stores, setStores] = useState<Store[]>([]);
  // const [bankWalletOffers, setBankWalletOffers] = useState<BankWalletOffer[]>(
  //   []
  // );
  const [loading, setLoading] = useState(true);
  const [contentLoading, setContentLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("usedby-desc");
  const [showAllOffers, setShowAllOffers] = useState(false); // New state for "Show More/Less"
  const router = useRouter();

  const offersSectionRef = React.useRef<HTMLDivElement>(null);

  // Load categories on mount
  useEffect(() => {
    const loadCategories = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await apiFetch<{ success: boolean; data: Category[] }>(
          "/categories/"
        );

        if (response.success && Array.isArray(response.data)) {
          const activeCategories = response.data.filter((cat) => cat.isActive);
          setCategories(activeCategories);

          if (activeCategories.length > 0) {
            const fashionCategory = activeCategories.find((cat) => cat.slug === (process.env.NEXT_PUBLIC_APP_TITLE === "OfferCounty" ? 'automotive-offers' : 'fashion'));
            setActive(fashionCategory ? fashionCategory.slug : activeCategories[0].slug);
          }
        } else {
          throw new Error("Invalid categories response");
        }
      } catch (err) {
        console.error("Error loading categories:", err);
        setError("Failed to load categories");
        setCategories([]);
      } finally {
        setLoading(false);
      }
    };

    loadCategories();
  }, []);

  // Load category content when active category changes
  useEffect(() => {
    if (!active) return;

    const loadCategoryContent = async () => {
      setContentLoading(true);
      setOffers([]);
      // setStores([]);
      // setBankWalletOffers([]);
      setError(null);

      try {
        const [offersRes] = await Promise.all([
          apiFetch<{ success: boolean; data: Offer[] }>(
            `/categories/slug/${active}/offers`
          ).catch(() => ({ success: false, data: [] })),
          apiFetch<{ success: boolean; data: Store[] }>(
            `/categories/slug/${active}/stores-with-offer-count`
          ).catch(() => ({ success: false, data: [] })),
          apiFetch<{ success: boolean; data: BankWalletOffer[] }>(
            `/categories/slug/${active}/bank-wallet-offers`
          ).catch(() => ({ success: false, data: [] })),
        ]);

        const fetchedOffers = Array.isArray(offersRes.data) ? offersRes.data : [];
        setOffers(fetchedOffers);
        // setStores(Array.isArray(storesRes.data) ? storesRes.data : []);
        // setBankWalletOffers(
        //   Array.isArray(bankWalletRes.data) ? bankWalletRes.data : []
        // );
      } catch (err) {
        console.error("Error loading category content:", err);
        setError("Failed to load category data");
      } finally {
        setContentLoading(false);
      }
    };

    loadCategoryContent();
  }, [active]);

  // Filtering and sorting logic
  const filteredOffers = offers
    .filter(
      (offer) =>
        offer.title.toLowerCase().includes(search.toLowerCase()) ||
        offer.store.toLowerCase().includes(search.toLowerCase())
    )
    .sort((a, b) => {
        switch (sort) {
            case "usedby-desc":
                return (b.usedBy ?? 0) - (a.usedBy ?? 0) || a.title.localeCompare(b.title);
            case "rating-desc":
                return (b.rating ?? 0) - (a.rating ?? 0);
            case "title-asc":
                return a.title.localeCompare(b.title);              
            default:
                return 0; 
        }
    });

  // Conditional Slicing for Mobile View (first 4 offers)
  const mobileOffersToDisplay = filteredOffers.slice(
    0,
    showAllOffers ? filteredOffers.length : MOBILE_OFFER_LIMIT
  );

  // const filteredStores = stores.filter((store) =>
  //   store.name.toLowerCase().includes(search.toLowerCase())
  // );

  // const filteredBankWalletOffers = bankWalletOffers.filter(
  //   (offer) =>
  //     offer.title.toLowerCase().includes(search.toLowerCase()) ||
  //     offer.bankName.toLowerCase().includes(search.toLowerCase())
  // );

  const handleCategoryChange = (categorySlug: string) => {
    setActive(categorySlug);
    setSearch(""); // Clear search when changing category
  };

  const handleShowLessClick = () => {
    setShowAllOffers(false);

    if (showAllOffers && offersSectionRef.current) {
      const offset = offersSectionRef.current.offsetTop - 100;

      window.scrollTo({
        top: offset > 0 ? offset : 0,
        behavior: "smooth"
      });
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col md:flex-row gap-6 bg-white rounded-xl shadow p-4 md:p-6">
        <LoadingState message="Loading categories..." />
      </div>
    );
  }

  if (error && categories.length === 0) {
    return (
      <div className="flex flex-col md:flex-row gap-6 bg-white rounded-xl shadow p-4 md:p-6">
        <EmptyState message={error} emoji="❌" />
      </div>
    );
  }

  return (
    <div className="flex flex-col md:flex-row lg:gap-6 px-4 lg:py-8">
      {/* Sidebar */}
      <aside className="md:w-64 w-full md:sticky lg:bg-white p-4 lg:rounded-xl lg:shadow-lg top-4 flex-shrink-0">
        <div className="hidden md:block mb-4">
          <h2 className="text-lg font-bold text-gray-900 mb-3">Categories</h2>
        </div>
        <div className="md:hidden mb-4 flex items-center justify-between">
          <div className="inline-block">
            <h2 className="relative text-2xl text-gray-700">Categories</h2>
            <hr className="h-1 border-0 bg-primary-500 mt-2 w-[70%] rounded-lg" />
          </div>
        </div>
        <nav className="flex md:flex-col flex-row gap-2 md:gap-3 overflow-x-auto md:overflow-x-visible">
          {categories.slice(0, 7).map((cat) => (
            <button
              key={cat.slug}
              value={cat.slug}
              className={`flex transition-all duration-200 font-medium text-xs line-clamp-1 truncate
                ${
                  active === cat.slug
                    ? "bg-primary-50 rounded-lg border-primary-200 text-primary-600 "
                    : "lg:bg-white border-transparent text-gray-700 hover:bg-gray-50 hover:border-gray-200"
                }
                flex-col items-center justify-center py-2 px-3 w-20 min-w-[5rem]
                md:flex-row md:items-center md:gap-3 md:px-4 md:py-3 md:w-full md:min-w-0 md:justify-start`}
              onClick={() => handleCategoryChange(cat.slug)}
              aria-current={active === cat.slug ? "page" : undefined}
            >
              <span
                className={`flex items-center justify-center w-8 h-8 rounded-full 
                  ${active === cat.slug ? "bg-primary-100" : "bg-transparent"}
                  mb-1 md:mb-0`}
              >
                {CATEGORY_ICONS[cat.name.toLowerCase()] || (
                  <Star className="w-5 h-5" />
                )}
              </span>
              <span
                className={`capitalize 
                  text-[10px] text-center block md:hidden w-full truncate`}
              >
                {cat.name}
              </span>

              {/* DESKTOP Category Name*/}
              <span className="hidden md:inline-block capitalize text-xs truncate">
                {cat.name}
              </span>
            </button>
          ))}
          {categories.length > 7 && (
            <Link
              href="/category"
              className="flex transition-all duration-200 font-medium text-sm whitespace-nowrap border-transparent text-gray-700 hover:bg-gray-50 hover:border-gray-200
                flex-col items-center justify-center py-2 px-3 w-20 min-w-[5rem]
                md:flex-row md:items-center md:gap-3 md:px-4 md:py-3 md:w-full md:min-w-0 md:justify-start"
              tabIndex={0}
            >
              <span className="flex items-center justify-center w-8 h-8 rounded-full bg-primary-100 mb-1 md:mb-0">
                <ArrowRightIcon className="w-4 h-4 text-primary-500" />{" "}
              </span>
              <span className="capitalize text-xs text-center block md:hidden text-primary-500">
                All
              </span>
              <span className="hidden md:inline-block capitalize text-primary-500 text-sm">
                View All
              </span>
            </Link>
          )}
        </nav>
        <div className="hidden md:block">
          <div className="mt-4 mb-6 w-full flex justify-center">
            {/* Crousal Banner Card */}
            <div className="w-full max-w-xs md:max-w-sm lg:max-w-md">
              <Carousel orientation="vertical" />
            </div>
          </div>
        </div>
      </aside>

      {/* Content */}
      <main className="flex-1 min-w-0">
        <div className="flex flex-row gap-4 mb-6 lg:p-4 lg:bg-white lg:rounded-lg">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search offers, stores, or banks..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 text-sm pr-4 py-2 text-gray-500 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
            />
          </div>

          <div className="relative flex-shrink-0">
            <div className="hidden md:block">
              <SlidersHorizontal className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <select
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                className="w-auto pl-10 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none bg-white"
              >
                <option value="usedby-desc">Most Used</option>
                <option value="rating-desc">Highest Rated</option>
                <option value="title-asc">Title (A-Z)</option>
              </select>
            </div>

            <div className="md:hidden">
              <MobileSortDropdown currentSort={sort} onSortChange={setSort} />
            </div>
          </div>
        </div>

        <div className="hidden md:block">
          <AdBanner />
        </div>
        {/* Offers Section */}
        <div className="lg:mb-8 " ref={offersSectionRef}>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-900">
              Offers{" "}
              {filteredOffers.length > 0 && (
                <span className="text-sm text-gray-500 font-normal">
                  ({filteredOffers.length})
                </span>
              )}
            </h2>
            <button
              onClick={() => router.push(`category/${active}`)}
              className="hidden md:block cursor-pointer text-primary-600 text-sm font-semibold hover:underline transition-colors"
            >
              Show All
            </button>
          </div>

          {contentLoading ? (
            <LoadingState message="Loading offers..." />
          ) : filteredOffers.length === 0 ? (
            <EmptyState
              message={
                search
                  ? "No offers found matching your search."
                  : "No offers found for this category."
              }
              emoji="🎁"
            />
          ) : (
            <>
              {/* Mobile View: Vertical list with Show More/Less logic */}
              <div className="flex flex-col gap-4 py-2 md:hidden">
                {mobileOffersToDisplay.map((offer, i) => (
                  <OfferCard
                    key={offer._id || i}
                    storeLogo={offer.storeIcon || "/assets/storeiconpng.png"}
                    storeName={offer.store}
                    title={offer.title}
                    description={offer.description || ""}
                    discountType={offer.discountType}
                    discountTypeFlat={offer.discountTypeFlat || ""}
                    discount={offer.discount || ""}
                    discountTypeUnit={offer.discountTypeUnit || ""}
                    oldValue={offer.oldValue || ""}
                    oldValueUnit={offer.oldValueUnit || "price"}
                    badge={offer.badge || ""}
                    validTill={offer.validTill}
                    slug={offer.slug || offer._id || ""}
                    allowRedirectUrl={offer.allowRedirectUrl}
                    redirectUrl={offer.redirectUrl}
                    rating={offer.rating}
                    couponCode={offer.couponCode}
                    usedBy={offer.usedBy}
                    // onActionClick={() => handleDealAction(offer)}
                  />
                ))}
              </div>

              {/* Desktop View: Existing two-row layout */}
              <div className="hidden md:block overflow-x-auto scrollbar-hide py-2 px-2">
                <div className="grid grid-flow-col grid-rows-2 gap-4">
                  {filteredOffers.map((offer, i) => (
                    <div
                      key={`desktop-${offer._id || i}`}
                      className="w-[300px] flex-shrink-0"
                    >
                      <OfferCard
                        storeLogo={
                          offer.storeIcon || "/assets/storeiconpng.png"
                        }
                        storeName={offer.store}
                        title={offer.title}
                        description={offer.description || ""}
                        discountType={offer.discountType}
                        discountTypeFlat={offer.discountTypeFlat || ""}
                        discount={offer.discount || ""}
                        discountTypeUnit={offer.discountTypeUnit || ""}
                        oldValue={offer.oldValue || ""}
                        oldValueUnit={offer.oldValueUnit || "price"}
                        badge={offer.badge || ""}
                        validTill={offer.validTill}
                        slug={offer.slug || offer._id || ""}
                        allowRedirectUrl={offer.allowRedirectUrl}
                        redirectUrl={offer.redirectUrl}
                        rating={offer.rating}
                        couponCode={offer.couponCode}
                        usedBy={offer.usedBy}
                        // onActionClick={() => handleDealAction(offer)}
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Show More/Less Button - Visible only on mobile, only if there are more than 4 offers  */}
              {filteredOffers.length > MOBILE_OFFER_LIMIT && (
                <div className="flex justify-center mt-4 md:hidden">
                  <button
                    onClick={
                      showAllOffers
                        ? handleShowLessClick
                        : () => setShowAllOffers(true)
                    }
                    className="flex items-center justify-center gap-1 text-sm font-semibold text-primary-600 px-4 py-2 rounded-lg hover:bg-primary-100 transition-colors w-full"
                  >
                    {showAllOffers
                      ? "Show Less"
                      : `Show ${
                          filteredOffers.length - MOBILE_OFFER_LIMIT
                        } More Offers`}
                    {showAllOffers ? (
                      <ChevronUp className="w-4 h-4" />
                    ) : (
                      <ChevronDown className="w-4 h-4" />
                    )}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
}